import { fetchApi } from '@/shared/api/fetchApi';
import { PaginatedResponse } from '@/shared/types/api';

export interface CarModel {
  id: string;
  slug: string;
  brand_name: string;
  model_name: string;
  transmission_type: string;
  body_type: string;
  tech_profile: string;
  description_en: string;
  description_fr: string;
  image: string;
  image_url: string;
  is_active: boolean;
}

export type CarModelsResponse = PaginatedResponse<CarModel>;
export interface TechProfile {
  id: string;
  name: string;
}

export type TechProfilesResponse = PaginatedResponse<TechProfile>;

export interface CarModelsParams {
  offset?: number;
  limit?: number;
}

export interface CreateCarModelDto {
  slug: string;
  brand_name: string;
  model_name: string;
  transmission_type: string;
  body_type: string;
  tech_profile: string;
  description_en: string;
  description_fr: string;
  is_active: boolean;
}

export const carModelsApi = {
  getCarModels: async (params: CarModelsParams = {}): Promise<CarModelsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.offset !== undefined) searchParams.append('offset', params.offset.toString());
    if (params.limit !== undefined) searchParams.append('limit', params.limit.toString());

    const url = `/api/v1/car_models/list?${searchParams.toString()}`;
    return fetchApi<CarModelsResponse>(url);
  },

  getCarModelById: async (id: string): Promise<CarModel> => {
    return fetchApi<CarModel>(`/api/v1/car_models/${id}`);
  },

  getTechProfiles: async (): Promise<TechProfilesResponse> => {
    return fetchApi<TechProfilesResponse>('/api/v1/tech_profiles/list');
  },

  createCarModel: async (data: CreateCarModelDto): Promise<CarModel> => {
    return fetchApi<CarModel>('/api/v1/car_models/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateCarModel: async (id: string, data: Partial<CarModel>): Promise<CarModel> => {
    return fetchApi<CarModel>(`/api/v1/car_models/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteCarModel: async (id: string): Promise<CarModel> => {
    return fetchApi<CarModel>(`/api/v1/car_models/${id}`, {
      method: 'DELETE',
    });
  },

  toggleCarModelActive: async (id: string, isActive: boolean): Promise<CarModel> => {
    return fetchApi<CarModel>(`/api/v1/car_models/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive }),
    });
  },

  uploadCarModelImage: async (id: string, file: File): Promise<CarModel> => {
    const formData = new FormData();
    formData.append('file', file);

    return fetchApi<CarModel>(`/api/v1/car_models/${id}/image/upload`, {
      method: 'POST',
      body: formData,
    });
  },
};
